/**
 * O que a tela de carregamento mostra, e quando ela aparece.
 *
 * ── Três coisas estavam erradas ───────────────────────────────────────────
 *
 * 1. Havia um `minDelay` de 1800ms. Todo refresh esperava 1,8 segundo olhando
 *    a tela mesmo com o dado já na mão. A tela existia para ser vista, e o
 *    preço era o app parecer lento sempre.
 *
 * 2. As mensagens giravam por RELÓGIO, uma a cada 1,5s, em laço. Depois de
 *    7,5 segundos ela via "Buscando suas transações..." de novo, tendo já
 *    lido "Preparando seu painel". Progresso encenado é da mesma família do
 *    número escrito pelo modelo: parece informação e não é.
 *
 * 3. O Render free hiberna a cada ~15 min de silêncio. A primeira carga
 *    depois disso leva dezenas de segundos, e a tela não tinha nada a dizer —
 *    repetia as cinco frases como se estivesse tudo normal.
 *
 * ── O que este módulo faz ─────────────────────────────────────────────────
 *
 * A mensagem passa a ser o que FALTA, de verdade: quem chamou informa o que
 * já chegou, e a frase é a do primeiro pendente. O texto é o mesmo de antes —
 * o que mudou é ele ter virado verdade.
 *
 * Módulo puro: recebe o que já chegou e o tempo decorrido, devolve o que
 * mostrar. Nada de timer aqui dentro.
 */

/** As quatro buscas da carga inicial, na ordem em que ela as espera. */
export const ETAPAS = [
  { chave: 'transacoes', texto: 'Buscando suas transações...' },
  { chave: 'metodos', texto: 'Carregando métodos de pagamento...' },
  { chave: 'investimentos', texto: 'Sincronizando investimentos...' },
  { chave: 'categorias', texto: 'Organizando categorias...' },
] as const;

export type ChaveDeEtapa = typeof ETAPAS[number]['chave'];

/*
 * ── Não há atraso antes de mostrar, e a tentativa de ter um foi um erro ──
 *
 * Cheguei a segurar a tela por 250ms, com o argumento de que carga rápida não
 * deveria piscar um overlay. O relato desmentiu na hora: "quando eu dou
 * refresh a tela de loading demora para aparecer".
 *
 * O argumento estava certo para o caso errado. Ele vale para um carregador
 * que surge no meio de uma tela JÁ montada. Aqui não: no boot, a tela da
 * sessão já está no ar, e o overlay dos dados é a CONTINUAÇÃO dela. Os 250ms
 * não evitavam piscada — abriam um buraco, e no buraco o app aparecia pela
 * metade antes de ser coberto de novo.
 *
 * A tela fica no ar do primeiro quadro até o dado chegar. Sem atraso para
 * aparecer, e sem tempo mínimo para sumir — o mínimo era o `minDelay` de
 * 1800ms que fazia todo refresh parecer lento.
 */

/**
 * A partir de quando avisar que a carga está demorando.
 *
 * O Render free hiberna com ~15 min de silêncio, e acordar leva dezenas de
 * segundos. Seis segundos sem NADA ter chegado não é rede lenta, é isso.
 *
 * ── Mas a mensagem não diz isso ──────────────────────────────────────────
 *
 * Ela dizia "o servidor estava dormindo e está acordando". É verdade, e é
 * verdade sobre uma coisa que só quem construiu o app entende — "servidor
 * dormindo" soa como defeito, não como plano de hospedagem. Quem lê fica
 * sabendo que algo está errado sem poder fazer nada com a informação.
 *
 * O que serve é o que muda a decisão dela: vai demorar, quanto, e que é para
 * esperar. Os 30 segundos ficam — sem eles a espera não tem fim declarado, e
 * ela não sabe se vale ficar ou voltar depois. Era a única parte da frase
 * antiga que respondia alguma coisa.
 */
export const SUSPEITA_DE_SONO_MS = 6_000;

export const MENSAGEM_ACORDANDO =
  'Estamos com uma lentidão hoje. Costuma levar uns 30 segundos, pode aguardar.';

export const MENSAGEM_FINAL = 'Preparando seu painel...';

export interface EstadoDeCarga {
  mensagem: string;
  /** Quantas das quatro já chegaram. Para a barra de progresso. */
  prontas: number;
  total: number;
  /** `true` quando nada chegou e já passou tempo demais. */
  acordando: boolean;
}

export const estadoDeCarga = (p: {
  /** As chaves do que JÁ chegou. */
  prontas: readonly string[];
  decorridoMs: number;
}): EstadoDeCarga => {
  const prontas = ETAPAS.filter(e => p.prontas.includes(e.chave)).length;
  const total = ETAPAS.length;

  /*
   * O aviso de lentidão só vale quando NADA chegou.
   *
   * Se uma resposta já voltou, o servidor está de pé e o que resta é rede ou
   * volume — avisar de lentidão geral ali seria falar de um sintoma que não é
   * o que está acontecendo.
   */
  const acordando = prontas === 0 && p.decorridoMs >= SUSPEITA_DE_SONO_MS;

  const pendente = ETAPAS.find(e => !p.prontas.includes(e.chave));
  const mensagem = acordando
    ? MENSAGEM_ACORDANDO
    : pendente
      ? pendente.texto
      : MENSAGEM_FINAL;

  return {
    mensagem,
    prontas,
    total,
    acordando,
  };
};
